import { ChatDashboard, ChatDashboardFilters } from '@if/mui'
import type { ChatDashboardFilters as ChatDashboardFiltersType, ChatDashboardEntry, ChatRoomLock } from '@if/core/types'
import { ECreditApplicationStatus, EMessageStatusFilter, ESortOrder } from '@if/core/types'
import type { GridPaginationModel } from '@mui/x-data-grid'
import FullScreenContainer from '@src/components/FullScreenContainer'
import {
  useGetActiveLocks,
  useGetMessageListEntries,
  useInvalidateDashboard,
} from '@src/data/api/chat-dashboard/chat-dashboard-api'
import { CURRENT_USER_PARTICIPANT_TYPE, SUPPORTED_INSTANT_MESSAGE_PROGRAMS } from '@src/data/types/constants'
import { getSignalRConnection, useSignalRConnectionReady } from '@src/data/api/chat-message/chat-message-signalr-api'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router-dom'
import { useFeatureFlags } from '@src/data/selectors/app-config-selectors'
import { getProgramsByCompanyId } from '@src/data/types/FinancingCompanies'
import { usePreferences } from '@src/providers/PreferencesProvider'
import { usePersistedFilters } from './usePersistedFilters'

const defaultFilters: ChatDashboardFiltersType = {
  searchTerm: undefined,
  messageStatus: EMessageStatusFilter.Unread,
  sortOrder: ESortOrder.OldestFirst,
}

const ChatDashboardPage = () => {
  const featureFlags = useFeatureFlags()

  const { t } = useTranslation()
  const navigate = useNavigate()
  const invalidateDashboard = useInvalidateDashboard()
  const { selectedCompany } = usePreferences()

  const [filters, setFilters] = usePersistedFilters(defaultFilters)
  const [selectedPrograms, setSelectedPrograms] = useState<string[] | undefined>(undefined)
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  })

  const offset = paginationModel.page * paginationModel.pageSize
  const limit = paginationModel.pageSize

  const filtersWithPrograms = useMemo<ChatDashboardFiltersType>(
    () => ({
      ...filters,
      financingPrograms: selectedPrograms as ChatDashboardFiltersType['financingPrograms'],
    }),
    [filters, selectedPrograms],
  )

  const [entries, isLoading] = useGetMessageListEntries(filtersWithPrograms, selectedCompany ?? '', offset, limit)

  useEffect(() => {
    setSelectedPrograms(undefined)
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
  }, [selectedCompany])

  // Fetch active locks for current page entries
  const entryIds = useMemo(() => entries.map((e) => e.id), [entries])
  const [activeLocks] = useGetActiveLocks(entryIds)

  // Maintain a locks map from batch fetch + real-time SignalR updates
  const [signalRLocks, setSignalRLocks] = useState<Record<string, ChatRoomLock>>({})

  const locksMap = useMemo(() => {
    const map: Record<string, ChatRoomLock> = {}
    activeLocks?.forEach((lock) => {
      map[lock.creditApplicationId] = lock
    })
    return { ...map, ...signalRLocks }
  }, [activeLocks, signalRLocks])

  // Listen for real-time lock changes and dashboard updates via SignalR
  const signalRReady = useSignalRConnectionReady()
  useEffect(() => {
    const connection = getSignalRConnection()
    if (!connection) return

    const handleLockChanged = (lock: ChatRoomLock) => {
      setSignalRLocks((prev) => ({
        ...prev,
        [lock.creditApplicationId]: lock,
      }))
    }

    connection.on('lockChanged', handleLockChanged)
    connection.on('messageUpdated', invalidateDashboard)

    return () => {
      connection.off('lockChanged', handleLockChanged)
      connection.off('messageUpdated', invalidateDashboard)
    }
  }, [invalidateDashboard, signalRReady])

  const programOptions = useMemo(
    () =>
      (selectedCompany
        ? getProgramsByCompanyId([selectedCompany]).filter((p) => SUPPORTED_INSTANT_MESSAGE_PROGRAMS.includes(p))
        : []
      ).map((p) => ({
        value: p,
        label: t(`EFinancingProgram.${p}`, { ns: 'enum' }),
      })),
    [t, selectedCompany],
  )

  const handleFiltersChange = useCallback(
    (updater: (prev: ChatDashboardFiltersType) => ChatDashboardFiltersType) => {
      setFilters(updater)
      setPaginationModel((prev) => ({ ...prev, page: 0 }))
    },
    [setFilters],
  )

  const handleSearchChange = useCallback(
    (searchTerm: string) => {
      setFilters((prev) => ({ ...prev, searchTerm: searchTerm || undefined }))
      setPaginationModel((prev) => ({ ...prev, page: 0 }))
    },
    [setFilters],
  )

  const handleProgramsChange = useCallback((programs: string[] | undefined) => {
    setSelectedPrograms(programs)
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
  }, [])

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel(model)
  }, [])

  const getRowClassName = useCallback(
    (params: { row: ChatDashboardEntry }) => {
      const { isUnread, type, applicationStatus } = params.row
      if (
        applicationStatus === ECreditApplicationStatus.Cancelled ||
        applicationStatus === ECreditApplicationStatus.Expired
      ) {
        return 'inactive-row'
      }
      return isUnread && type !== CURRENT_USER_PARTICIPANT_TYPE ? 'unread-row' : ''
    },
    [],
  )

  const handleConversationSelect = useCallback(
    (entry: ChatDashboardEntry) => {
      navigate(`/chat-dashboard/${entry.financingProgram}/${entry.id}`, {
        state: {
          applicationStatus: entry.applicationStatus,
          language: entry.language,
          referenceNumber: String(entry.referenceNumber),
          customerFullName: entry.customerFullName,
          merchantName: entry.merchantName,
        },
      })
    },
    [navigate],
  )

  if (!featureFlags.useInstantMessage) return <Navigate to="/applications/browse" replace />

  return (
    <FullScreenContainer>
      <ChatDashboard
        entries={entries}
        totalCount={entries.length}
        isLoading={isLoading}
        searchTerm={filters.searchTerm ?? ''}
        onSearchChange={handleSearchChange}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        onConversationSelect={handleConversationSelect}
        locks={locksMap}
        getRowClassName={getRowClassName}
        title={t('instantMessage.chatDashboardTitle')}
        filtersSlot={
          <ChatDashboardFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            showLanguageFilter
            programOptions={programOptions}
            selectedPrograms={selectedPrograms}
            onProgramsChange={handleProgramsChange}
          />
        }
      />
    </FullScreenContainer>
  )
}

export default memo(ChatDashboardPage)
